import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowLeft, Clock, Calendar, Tag, TrendingDown, TrendingUp, ListChecks, ArrowRight, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '../sections/Navbar';
import Footer from '../sections/Footer';

gsap.registerPlugin(ScrollTrigger);

const blogPosts = [
  {
    title: 'AI mění výsledky vyhledávání: proč klesají kliky a co s tím',
    excerpt: 'Dřív stačilo být na první stránce Googlu. Dnes uživatelé často dostanou odpověď přímo ve výsledcích — a vůbec nekliknou. Proč to tak je a jak se přizpůsobit?',
    slug: 'ai-meni-vysledky-vyhledavani',
    readTime: '8 min',
    category: 'AI SEO',
    color: '#0ea5e9',
    icon: TrendingDown,
    image: '/blog/article-1-hero.jpg',
  },
  {
    title: 'Proč poroste AI vyhledávání: 6 důvodů a dopad na weby',
    excerpt: 'AI vyhledávání roste, protože řeší skutečný problém: lidé nechtějí procházet desítky stránek. Chtějí rychlou odpověď, která jim pomůže se rozhodnout.',
    slug: 'proc-lide-budou-vic-vyuzivat-ai-vyhledavani',
    readTime: '6 min',
    category: 'Trendy',
    color: '#00d4aa',
    icon: TrendingUp,
    image: '/blog/article-2-hero.jpg',
  },
  {
    title: 'AI SEO bez chaosu: 7 priorit pro první měsíc',
    excerpt: 'AI SEO se dá dělat roky a nikam se neposunout. Nebo se dá udělat pár kroků ve správném pořadí a rychle zjistit, co funguje. Tento plán je pro vás.',
    slug: 'ai-seo-bez-chaosu-prvni-mesic',
    readTime: '10 min',
    category: 'Průvodce',
    color: '#14b8a6',
    icon: ListChecks,
    image: '/blog/article-3-hero.jpg',
  },
];

// Komponenta pro obrázek s fallbackem
const BlogImage = ({ src, alt }: { src: string; alt: string }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#0ea5e9]/20 to-[#00d4aa]/20">
        <ImageIcon className="w-12 h-12 text-slate-400" />
      </div>
    );
  }

  return (
    <>
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-100">
          <div className="w-8 h-8 border-2 border-[#0ea5e9]/30 border-t-[#0ea5e9] rounded-full animate-spin" />
        </div>
      )}
      <img 
        src={src} 
        alt={alt}
        className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 ${loaded ? 'opacity-100' : 'opacity-0'}`}
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
      />
    </>
  );
};

const BlogPage = () => {
  const pageRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Malé zpoždění pro správnou inicializaci ScrollTrigger
    const timer = setTimeout(() => {
      const ctx = gsap.context(() => {
        gsap.from(headerRef.current, {
          opacity: 0,
          y: 30,
          duration: 0.8,
          ease: 'power2.out',
        });

        const cards = cardsRef.current?.querySelectorAll('.blog-card');
        if (cards && cards.length > 0) {
          gsap.from(cards, {
            scrollTrigger: {
              trigger: cardsRef.current,
              start: 'top 85%',
              toggleActions: 'play none none reverse',
            },
            opacity: 0,
            y: 50,
            duration: 0.7,
            stagger: 0.12,
            ease: 'power2.out',
          });
        }
      }, pageRef);

      return () => ctx.revert();
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div ref={pageRef} className="min-h-screen bg-gradient-to-br from-sky-50 via-cyan-50 to-emerald-50">
      <Navbar />
      
      <main className="relative z-10 pt-24 pb-16">
        {/* Header */}
        <div ref={headerRef} className="px-4 sm:px-6 lg:px-8 mb-12">
          <div className="max-w-7xl mx-auto">
            <Link to="/">
              <Button
                variant="ghost"
                className="mb-6 text-slate-500 hover:text-slate-700 -ml-4"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Zpět na hlavní stránku
              </Button>
            </Link>
            
            <div className="text-center max-w-3xl mx-auto">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-6">
                <Tag className="w-4 h-4 text-[#0ea5e9]" />
                <span className="text-sm text-slate-600">Blog</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-slate-800">
                AI SEO <span className="text-gradient">Průvodce</span>
              </h1>
              
              <p className="text-xl text-slate-500">
                Praktické návody, analýzy a tipy pro optimalizaci vašeho webu 
                pro AI vyhledávače. Buďte vidět v ChatGPT, Perplexity a dalších.
              </p>
            </div>
          </div>
        </div>

        {/* Blog Cards Grid */}
        <div className="px-4 sm:px-6 lg:px-8">
          <div ref={cardsRef} className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {blogPosts.map((post, index) => {
                const Icon = post.icon;
                return (
                  <article
                    key={index}
                    className="blog-card group relative rounded-2xl overflow-hidden bg-white/60 backdrop-blur-xl border border-white/50 shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-500 flex flex-col"
                  >
                    {/* Image */}
                    <div className="relative h-48 overflow-hidden bg-slate-100">
                      <BlogImage src={post.image} alt={post.title} />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent pointer-events-none" />
                      
                      {/* Category Badge */}
                      <div 
                        className="absolute top-4 left-4 flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium z-10"
                        style={{
                          backgroundColor: `${post.color}30`,
                          color: post.color,
                          backdropFilter: 'blur(8px)',
                        }}
                      >
                        <Tag className="w-3 h-3" />
                        {post.category}
                      </div>
                    </div>

                    {/* Content */}
                    <div className="relative z-10 p-6 flex flex-col flex-grow">
                      {/* Meta */}
                      <div className="flex items-center gap-4 mb-4 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          6. února 2026
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {post.readTime}
                        </span>
                      </div>

                      {/* Icon */}
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center mb-4"
                        style={{
                          background: `linear-gradient(135deg, ${post.color}20, ${post.color}10)`,
                          border: `1px solid ${post.color}30`,
                        }}
                      >
                        <Icon className="w-5 h-5" style={{ color: post.color }} />
                      </div>

                      {/* Title */}
                      <h2 className="text-xl font-bold mb-3 text-slate-800 group-hover:text-gradient transition-colors line-clamp-2">
                        {post.title}
                      </h2>

                      {/* Excerpt */}
                      <p className="text-slate-500 text-sm leading-relaxed mb-6 flex-grow line-clamp-3">
                        {post.excerpt}
                      </p>

                      {/* CTA */}
                      <Link 
                        to={`/blog/${post.slug}`}
                        className="flex items-center gap-2 text-sm font-medium group/btn"
                        style={{ color: post.color }}
                      >
                        Číst článek
                        <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                      </Link>
                    </div>

                    {/* Bottom Border Gradient */}
                    <div
                      className="absolute bottom-0 left-0 right-0 h-1 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                      style={{
                        background: `linear-gradient(90deg, transparent, ${post.color}, transparent)`,
                      }}
                    />
                  </article>
                );
              })}
            </div>
          </div>
        </div>

        {/* Newsletter CTA */}
        <div className="px-4 sm:px-6 lg:px-8 mt-16">
          <div className="max-w-3xl mx-auto">
            <div className="glass-card rounded-2xl p-8 text-center border border-[#0ea5e9]/20">
              <h3 className="text-2xl font-bold mb-4 text-slate-800">Chcete dostávat nové články?</h3>
              <p className="text-slate-500 mb-6">
                Přihlaste se k odběru a buďte mezi prvními, kdo získá nové tipy pro AI SEO.
              </p>
              <Link to="/#newsletter">
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-[#0ea5e9] to-[#00d4aa] text-white font-semibold px-8"
                >
                  Přihlásit se k newsletteru
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default BlogPage;
